import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, CalendarClock, ChevronDown, Gauge, X } from "lucide-react";
import { toast } from "sonner";
import { getMonthlyCategoryTransactions, getMonthSummary } from "@/lib/api";
import { categoryColor, formatEUR, formatEUR0 } from "@/lib/format";

const CYAN = "#00D4FF";
const GREEN = "#00FF87";
const PINK = "#FF7A9C";

const PACE_COLOR = { ok: GREEN, warning: "#F5A623", over: "#FF4D4D", none: "rgba(255,255,255,0.4)" };

const fmtDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
};

/* Riga categoria con drill-down sulle transazioni del mese corrente. */
const CategoryRow = ({ month, category, amount, kind, accent, isOpen, onToggle, detail }) => (
  <div className="overflow-hidden rounded-[8px] border border-white/[0.05] bg-[#050508]">
    <button
      onClick={onToggle}
      data-testid="month-category-row"
      data-kind={kind}
      aria-expanded={isOpen}
      className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-white/[0.03] active:bg-white/[0.05]"
    >
      <span className="flex min-w-0 items-center gap-2">
        <ChevronDown
          size={13}
          className={`shrink-0 text-white/30 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: categoryColor(category) }} />
        <span className="truncate text-[12px] font-light text-white/80">{category}</span>
      </span>
      <span className="mono shrink-0 text-[12px] tabular-nums" style={{ color: accent }}>
        {formatEUR(amount)}
      </span>
    </button>
    {isOpen && (
      <div className="border-t border-white/[0.05] px-3 py-2" data-testid="month-category-detail">
        {!detail || detail.loading ? (
          <div className="space-y-1.5 py-1">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-3 w-full animate-pulse rounded bg-white/[0.05]" />
            ))}
          </div>
        ) : detail.transactions.length === 0 ? (
          <p className="py-2 text-center text-[11px] font-light text-white/35">Nessuna transazione</p>
        ) : (
          <>
            {detail.count > detail.transactions.length && (
              <p className="caps-tag pb-1.5 text-white/30" style={{ fontSize: "9px" }}>
                {detail.transactions.length} di {detail.count} movimenti
              </p>
            )}
            <div className="space-y-1.5">
              {detail.transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-2" data-testid="month-category-tx">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="mono shrink-0 text-[10px] uppercase text-white/30">{fmtDay(t.date)}</span>
                    <span className="truncate text-[11px] font-light text-white/65">{t.description}</span>
                  </span>
                  <span className="mono shrink-0 text-[11px] tabular-nums text-white/60">{formatEUR(t.amount)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    )}
  </div>
);

// Sheet "Questo mese": entrate + uscite del mese CORRENTE (in corso), saldo del mese,
// ripartizione per categoria (con drill-down sulle transazioni) e ritmo/proiezione.
export const MonthSheet = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null); // chiave "kind:category" aperta
  const [catTx, setCatTx] = useState({}); // cache: "kind:category" -> {loading, transactions, count, total}

  const load = useCallback(async () => {
    setData(null);
    setExpanded(null);
    setCatTx({});
    try {
      setData(await getMonthSummary());
    } catch {
      toast.error("Impossibile caricare il riepilogo del mese");
    }
  }, []);

  const toggleCat = async (category, kind) => {
    const key = `${kind}:${category}`;
    if (expanded === key) {
      setExpanded(null);
      return;
    }
    setExpanded(key);
    if (!catTx[key] && data?.month) {
      setCatTx((prev) => ({ ...prev, [key]: { loading: true } }));
      try {
        const res = await getMonthlyCategoryTransactions(data.month, category, kind);
        setCatTx((prev) => ({
          ...prev,
          [key]: { loading: false, transactions: res.transactions || [], count: res.count, total: res.total },
        }));
      } catch {
        setCatTx((prev) => ({ ...prev, [key]: { loading: false, transactions: [], count: 0 } }));
        toast.error("Impossibile caricare il dettaglio della categoria");
      }
    }
  };

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      load();
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const pace = data?.pace;
  const net = data?.net ?? 0;
  const netColor = net >= 0 ? GREEN : "#FF4D4D";
  const paceColor = PACE_COLOR[pace?.status] || PACE_COLOR.none;
  const projPct = pace && pace.budget > 0 ? Math.min((pace.projected_spend / pace.budget) * 100, 100) : 0;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid="month-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 top-12 mx-auto flex max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#030305]"
          >
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3 pt-3">
              <span className="mono flex items-center gap-2 text-sm font-medium tracking-[0.1em]" style={{ color: CYAN }}>
                <CalendarClock size={15} />
                QUESTO MESE <span className="text-white/25">//</span> LIQO
              </span>
              <button
                onClick={onClose}
                data-testid="month-close-button"
                aria-label="Chiudi"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
              >
                <X size={14} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {data === null ? (
                <div className="space-y-4" data-testid="month-loading">
                  <div className="h-36 animate-pulse rounded-[12px] border border-white/[0.06] bg-[#0D0D14]" />
                  <div className="h-24 animate-pulse rounded-[12px] border border-white/[0.06] bg-[#0D0D14]" />
                  <div className="h-32 animate-pulse rounded-[12px] border border-white/[0.06] bg-[#0D0D14]" />
                </div>
              ) : (
                <>
                  {/* hero: saldo del mese */}
                  <div
                    className="rounded-[12px] border bg-[#0D0D14] p-4"
                    style={{ borderColor: `${CYAN}33`, boxShadow: `0 0 0 1px ${CYAN}14` }}
                  >
                    <div className="flex items-center justify-between">
                      <p className="caps-tag" style={{ color: CYAN, opacity: 0.85 }}>
                        Saldo del mese · {data.label}
                      </p>
                      <span className="mono caps-tag text-white/30" data-testid="month-tx-count">
                        {data.tx_count} mov.
                      </span>
                    </div>
                    <p
                      className="mono mt-2 text-[40px] font-light leading-none tracking-[-1.5px] tabular"
                      style={{ color: netColor }}
                      data-testid="month-net"
                    >
                      {net >= 0 ? "+" : "−"}
                      {formatEUR0(Math.abs(net))}
                    </p>
                    {data.income > 0 && (
                      <p className="mono mt-2.5 text-[12px] text-white/45" data-testid="month-rate">
                        Tasso di risparmio {data.savings_rate}%
                      </p>
                    )}
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-[8px] border border-white/[0.06] bg-[#050508] px-3 py-2.5">
                        <p className="caps-tag flex items-center gap-1 text-white/35">
                          <ArrowUpRight size={11} style={{ color: GREEN }} /> Entrate
                        </p>
                        <p className="mono mt-1 text-[15px] font-light tabular" style={{ color: GREEN }} data-testid="month-income">
                          {formatEUR0(data.income)}
                        </p>
                      </div>
                      <div className="rounded-[8px] border border-white/[0.06] bg-[#050508] px-3 py-2.5">
                        <p className="caps-tag flex items-center gap-1 text-white/35">
                          <ArrowDownRight size={11} style={{ color: PINK }} /> Uscite
                        </p>
                        <p className="mono mt-1 text-[15px] font-light tabular" style={{ color: PINK }} data-testid="month-expenses">
                          {formatEUR0(data.expenses)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ritmo / proiezione fine mese */}
                  {pace && pace.budget > 0 && (
                    <div className="mt-4 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4" data-testid="month-pace">
                      <div className="flex items-center justify-between">
                        <p className="caps-tag flex items-center gap-1.5 text-white/45">
                          <Gauge size={13} style={{ color: paceColor }} /> Ritmo di spesa
                        </p>
                        <span className="mono text-[11px] tabular-nums text-white/40">{pace.days_left} gg alla fine</span>
                      </div>
                      <div className="mt-3 flex items-end justify-between">
                        <div>
                          <p className="caps-tag text-white/35">Speso finora</p>
                          <p className="mono mt-0.5 text-[18px] font-light tabular text-white/90">{formatEUR0(pace.spent)}</p>
                        </div>
                        <div className="text-right">
                          <p className="caps-tag text-white/35">Proiezione fine mese</p>
                          <p className="mono mt-0.5 text-[18px] font-light tabular" style={{ color: paceColor }} data-testid="month-projection">
                            {formatEUR0(pace.projected_spend)}
                          </p>
                        </div>
                      </div>
                      {/* barra budget */}
                      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
                        <div className="h-full rounded-full" style={{ width: `${projPct}%`, background: paceColor }} />
                      </div>
                      <p className="mono mt-2 text-[11px] text-white/40">
                        Budget {formatEUR0(pace.budget)} · {pace.pct_projected}% previsto
                      </p>
                    </div>
                  )}

                  {!data.has_data && (
                    <div className="mt-4 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4 text-center" data-testid="month-empty">
                      <p className="mono text-[13px] font-light text-white/70">Nessun movimento questo mese</p>
                      <p className="mt-1.5 text-[12px] font-light text-white/40">
                        Appena arrivano entrate e spese di {data.month_name}, le vedrai qui.
                      </p>
                    </div>
                  )}

                  {/* uscite per categoria */}
                  {data.expense_categories?.length > 0 && (
                    <div className="mt-4 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4" data-testid="month-expense-categories">
                      <div className="mb-2.5 flex items-baseline justify-between">
                        <p className="caps-tag text-white/45">Uscite per categoria</p>
                        <span className="caps-tag text-white/25" style={{ fontSize: "9px" }}>tocca per dettaglio</span>
                      </div>
                      <div className="space-y-1.5">
                        {data.expense_categories.map((c) => {
                          const key = `expense:${c.category}`;
                          return (
                            <CategoryRow
                              key={key}
                              month={data.month}
                              category={c.category}
                              amount={-c.amount}
                              kind="expense"
                              accent={PINK}
                              isOpen={expanded === key}
                              onToggle={() => toggleCat(c.category, "expense")}
                              detail={catTx[key]}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* entrate per categoria */}
                  {data.income_categories?.length > 0 && (
                    <div className="mt-4 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4" data-testid="month-income-categories">
                      <div className="mb-2.5 flex items-baseline justify-between">
                        <p className="caps-tag text-white/45">Entrate per categoria</p>
                        <span className="caps-tag text-white/25" style={{ fontSize: "9px" }}>tocca per dettaglio</span>
                      </div>
                      <div className="space-y-1.5">
                        {data.income_categories.map((c) => {
                          const key = `income:${c.category}`;
                          return (
                            <CategoryRow
                              key={key}
                              month={data.month}
                              category={c.category}
                              amount={c.amount}
                              kind="income"
                              accent={GREEN}
                              isOpen={expanded === key}
                              onToggle={() => toggleCat(c.category, "income")}
                              detail={catTx[key]}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <p className="mono mt-4 px-1 text-center text-[10px] leading-4 tracking-[0.04em] text-white/25">
                    Mese in corso · i numeri si aggiornano a ogni nuova transazione.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
